/**
 * MyApplications - User Dashboard to Track Application Status
 * Shows all applications (PPV, Business, PUDO) submitted by the user
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Truck,
  Store,
  MapPin,
  Calendar,
  ChevronRight,
  RefreshCw,
  Plus,
  Eye
} from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-blue-500', icon: Clock, textColor: 'text-blue-600' },
  submitted: { label: 'Submitted', color: 'bg-blue-500', icon: Clock, textColor: 'text-blue-600' },
  under_review: { label: 'Under Review', color: 'bg-amber-500', icon: AlertCircle, textColor: 'text-amber-600' },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle, textColor: 'text-green-600' },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle, textColor: 'text-red-600' },
  incomplete: { label: 'Incomplete', color: 'bg-gray-500', icon: AlertCircle, textColor: 'text-gray-600' }
};

const TYPE_CONFIG = {
  ppv: { label: 'PPV Driver', icon: Truck, color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
  driver: { label: 'PPV Driver', icon: Truck, color: 'text-emerald-500', bgColor: 'bg-emerald-50' },
  business: { label: 'Business Seller', icon: Store, color: 'text-blue-500', bgColor: 'bg-blue-50' },
  pudo: { label: 'PUDO Centre', icon: MapPin, color: 'text-purple-500', bgColor: 'bg-purple-50' }
};

export default function MyApplications() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [applications, setApplications] = useState([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/my-applications' } });
      return;
    }
    fetchApplications();
  }, [isAuthenticated, navigate]);

  const fetchApplications = async () => {
    try {
      const response = await api.get('/applications/my-applications');
      setApplications(response.data || []);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
      // Try alternative endpoints
      try {
        const [ppv, business, pudo] = await Promise.all([
          api.get('/ppv-applications/my-application').catch(() => ({ data: null })),
          api.get('/business-applications/my-application').catch(() => ({ data: null })),
          api.get('/pudo-applications/my-application').catch(() => ({ data: null }))
        ]);
        
        const apps = [];
        if (ppv.data) apps.push({ ...ppv.data, type: 'ppv' });
        if (business.data) apps.push({ ...business.data, type: 'business' });
        if (pudo.data) apps.push({ ...pudo.data, type: 'pudo' });
        setApplications(apps);
      } catch (e) {
        console.error('Failed to fetch individual applications:', e);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchApplications();
    toast.success('Applications refreshed');
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.pending;
  };

  const getTypeConfig = (type) => {
    return TYPE_CONFIG[type?.toLowerCase()] || TYPE_CONFIG.business;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-JM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-primary-foreground px-6 py-4 sticky top-0 z-50 shadow-md">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="hover:bg-primary-foreground/10">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-bold text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                My Applications
              </h1>
              <p className="text-sm text-primary-foreground/70">Track your application status</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="hover:bg-primary-foreground/10"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="text-center p-4">
            <p className="text-3xl font-bold text-primary">{applications.length}</p>
            <p className="text-sm text-muted-foreground">Total Applications</p>
          </Card>
          <Card className="text-center p-4">
            <p className="text-3xl font-bold text-amber-500">
              {applications.filter(a => ['pending', 'submitted', 'under_review'].includes(a.status?.toLowerCase())).length}
            </p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </Card>
          <Card className="text-center p-4">
            <p className="text-3xl font-bold text-green-500">
              {applications.filter(a => a.status?.toLowerCase() === 'approved').length}
            </p>
            <p className="text-sm text-muted-foreground">Approved</p>
          </Card>
        </div>

        {/* Applications List */}
        {applications.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold mb-2">No Applications Yet</h3>
            <p className="text-muted-foreground mb-6">Start your journey with Liantiao by submitting an application!</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate('/driver/apply')} className="bg-emerald-500 hover:bg-emerald-600">
                <Truck className="w-4 h-4 mr-2" />
                Apply as PPV Driver
              </Button>
              <Button onClick={() => navigate('/seller/apply')} variant="outline">
                <Store className="w-4 h-4 mr-2" />
                Apply as Business
              </Button>
              <Button onClick={() => navigate('/pudo/apply')} variant="outline">
                <MapPin className="w-4 h-4 mr-2" />
                Apply as PUDO Centre
              </Button>
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {applications.map((app, index) => {
              const statusConfig = getStatusConfig(app.status);
              const typeConfig = getTypeConfig(app.type || app.application_type);
              const StatusIcon = statusConfig.icon;
              const TypeIcon = typeConfig.icon;
              
              return (
                <Card 
                  key={app.id || index} 
                  className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/application/${app.type || app.application_type}/${app.id}`)}
                >
                  <CardContent className="p-0">
                    <div className="flex items-stretch">
                      {/* Type Indicator */}
                      <div className={`w-2 ${statusConfig.color}`} />
                      
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-4">
                            {/* Type Icon */}
                            <div className={`w-12 h-12 rounded-xl ${typeConfig.bgColor} flex items-center justify-center shrink-0`}>
                              <TypeIcon className={`w-6 h-6 ${typeConfig.color}`} />
                            </div>
                            
                            {/* Details */}
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-semibold text-lg">{typeConfig.label} Application</h3>
                                <Badge className={`${statusConfig.color} text-white`}>
                                  <StatusIcon className="w-3 h-3 mr-1" />
                                  {statusConfig.label}
                                </Badge>
                              </div>
                              
                              <p className="text-sm text-muted-foreground mb-2">
                                {app.business_name || app.full_name || app.name || user?.name || 'Your Application'}
                              </p>
                              
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Submitted: {formatDate(app.created_at || app.submitted_at)}
                                </span>
                                {app.updated_at && app.updated_at !== app.created_at && (
                                  <span className="flex items-center gap-1">
                                    <RefreshCw className="w-3 h-3" />
                                    Updated: {formatDate(app.updated_at)}
                                  </span>
                                )}
                              </div>
                              
                              {/* Rejection reason */}
                              {app.status?.toLowerCase() === 'rejected' && app.rejection_reason && (
                                <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
                                  <p className="text-sm text-red-700">
                                    <strong>Reason:</strong> {app.rejection_reason}
                                  </p>
                                </div>
                              )}
                              
                              {/* Admin notes */}
                              {app.admin_notes && (
                                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                                  <p className="text-sm text-blue-700">
                                    <strong>Note from Admin:</strong> {app.admin_notes}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Action */}
                          <div className="flex items-center text-muted-foreground">
                            <Eye className="w-4 h-4 mr-1" />
                            <ChevronRight className="w-5 h-5" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Apply for More */}
        {applications.length > 0 && (
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Plus className="w-5 h-5 text-primary" />
              Apply for More Opportunities
            </h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {!applications.find(a => a.type === 'ppv' || a.type === 'driver') && (
                <Button variant="outline" className="justify-start" onClick={() => navigate('/driver/apply')}>
                  <Truck className="w-4 h-4 mr-2 text-emerald-500" />
                  PPV Driver
                </Button>
              )}
              {!applications.find(a => a.type === 'business') && (
                <Button variant="outline" className="justify-start" onClick={() => navigate('/seller/apply')}>
                  <Store className="w-4 h-4 mr-2 text-blue-500" />
                  Business Seller
                </Button>
              )}
              {!applications.find(a => a.type === 'pudo') && (
                <Button variant="outline" className="justify-start" onClick={() => navigate('/pudo/apply')}>
                  <MapPin className="w-4 h-4 mr-2 text-purple-500" />
                  PUDO Centre
                </Button>
              )}
            </div>
          </Card>
        )}
      </main>
    </div>
  );
}
